import * as THREE from 'three';

/**
 * Classic RTS camera: orbiting a ground target with smooth pan, zoom,
 * optional rotation and tilt, bookmarks and follow. Independent of the
 * simulation; updated every rendered frame.
 */
export class RTSCamera {
  readonly camera: THREE.PerspectiveCamera;
  target = new THREE.Vector3(32, 0, 32);
  private goal = new THREE.Vector3(32, 0, 32);
  distance = 20;
  private goalDistance = 20;
  yaw = 0;
  private goalYaw = 0;
  pitch = 0.88;
  private goalPitch = 0.88;
  minDistance = 6;
  maxDistance = 70;
  bounds = { minX: 0, minZ: 0, maxX: 128, maxZ: 128 };
  follow: (() => THREE.Vector3 | null) | null = null;
  bookmarks: (THREE.Vector3 | null)[] = [null, null, null, null];
  heightAt: (x: number, z: number) => number = () => 0;

  constructor(aspect: number) {
    this.camera = new THREE.PerspectiveCamera(38, aspect, 0.5, 2000);
  }

  setBounds(size: number): void {
    this.bounds = { minX: 0, minZ: 0, maxX: size, maxZ: size };
  }

  /** Pan in screen-relative directions (dx right, dz down-screen), in world units. */
  pan(dx: number, dz: number): void {
    // While following a unit the view is locked to it: scrolling does not pull it away.
    if (this.follow) return;
    const c = Math.cos(this.yaw);
    const s = Math.sin(this.yaw);
    const k = this.distance / 26;
    this.goal.x += (dx * c + dz * s) * k;
    this.goal.z += (-dx * s + dz * c) * k;
    this.follow = null;
    this.clampGoal();
  }

  zoom(delta: number): void {
    this.goalDistance = Math.min(this.maxDistance, Math.max(this.minDistance, this.goalDistance * Math.pow(1.0015, delta)));
  }

  rotate(d: number): void {
    this.goalYaw += d;
  }

  tilt(d: number): void {
    this.goalPitch = Math.min(1.35, Math.max(0.55, this.goalPitch + d));
  }

  resetView(): void {
    this.goalYaw = 0;
    this.goalPitch = 0.88;
    this.goalDistance = 20;
  }

  jumpTo(x: number, z: number, immediate = false): void {
    this.goal.set(x, 0, z);
    this.follow = null;
    this.clampGoal();
    if (immediate) this.target.copy(this.goal);
  }

  setBookmark(i: number): void {
    this.bookmarks[i] = this.goal.clone();
  }

  recallBookmark(i: number): void {
    const b = this.bookmarks[i];
    if (b) this.jumpTo(b.x, b.z);
  }

  private clampGoal(): void {
    const b = this.bounds;
    this.goal.x = Math.min(b.maxX, Math.max(b.minX, this.goal.x));
    this.goal.z = Math.min(b.maxZ, Math.max(b.minZ, this.goal.z));
  }

  update(dt: number): void {
    if (this.follow) {
      const p = this.follow();
      if (p) {
        this.goal.x = p.x;
        this.goal.z = p.z;
        this.clampGoal();
      } else this.follow = null;
    }
    const k = 1 - Math.exp(-dt * 10);
    this.target.x += (this.goal.x - this.target.x) * k;
    this.target.z += (this.goal.z - this.target.z) * k;
    const gh = this.heightAt(this.target.x, this.target.z);
    this.target.y += (Math.max(0, gh) - this.target.y) * k;
    this.distance += (this.goalDistance - this.distance) * k;
    this.yaw += (this.goalYaw - this.yaw) * k;
    this.pitch += (this.goalPitch - this.pitch) * k;
    const h = Math.sin(this.pitch) * this.distance;
    const r = Math.cos(this.pitch) * this.distance;
    this.camera.position.set(this.target.x + Math.sin(this.yaw) * r, this.target.y + h, this.target.z + Math.cos(this.yaw) * r);
    this.camera.lookAt(this.target);
  }

  /** World-space corners of the view on the ground plane (for the minimap). */
  groundFrustum(ground: (ndcX: number, ndcY: number) => THREE.Vector3 | null): THREE.Vector3[] {
    const pts: THREE.Vector3[] = [];
    for (const [x, y] of [[-1, -1], [1, -1], [1, 1], [-1, 1]]) {
      const p = ground(x, y);
      if (p) pts.push(p);
    }
    return pts;
  }
}
